import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { ArrowRight } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { useCreateWorkspace } from '../../../entities/workspace/mutation/use-create-workspace';
import { useWorkspaceCreationStorageState } from '../hooks/use-workspace-creation-storage-state';

import { useUser } from '@/entities/auth/hooks/use-user';
import { Button } from '@/shared/components/shadcn/button';
import { Input } from '@/shared/components/shadcn/input';
import { showErrorDialog } from '@/shared/utils/show-error-dialog';
import { getPageRoute } from '@/entities/auth/utils/get-login-redirect-path';

const userInfoSchema = z.object({
  companyName: z.string().min(1, 'Company name is required'),
  jobTitle: z.string().min(1, 'Job title is required'),
});

type UserInfoFormData = z.infer<typeof userInfoSchema>;

export function UserInfoStep() {
  const redirectPage = useSearch({
    from: '/workspace/create/user-info',
    select: (search) => search.redirectPage,
  });
  const navigate = useNavigate();
  const [workspaceData, setWorkspaceData] = useWorkspaceCreationStorageState();
  const { refetch: refetchUser } = useUser();

  const form = useForm<UserInfoFormData>({
    resolver: zodResolver(userInfoSchema),
    mode: 'onChange',
    defaultValues: {
      companyName: workspaceData.companyName || '',
      jobTitle: workspaceData.jobTitle || '',
    },
  });

  const createWorkspaceMutation = useCreateWorkspace();

  const handleSubmit = async (data: UserInfoFormData) => {
    setWorkspaceData((prev) => ({
      ...prev,
      companyName: data.companyName,
      jobTitle: data.jobTitle,
    }));

    try {
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

      const response = await createWorkspaceMutation.mutateAsync({
        workspaceName: workspaceData.workspaceName,
        language: workspaceData.language,
        timezone,
        companyName: data.companyName,
        jobTitle: data.jobTitle,
      });

      setWorkspaceData({
        workspaceName: '',
        language: '',
        companyName: undefined,
        jobTitle: undefined,
      });

      await refetchUser();

      navigate(getPageRoute(response.id, redirectPage));
    } catch (error) {
      console.error('Failed to create workspace:', error);

      await showErrorDialog({
        error,
        title: 'Failed to Create Workspace',
        message:
          "We couldn't create your workspace. Please try again or contact our support team if the problem persists.",
      });
    }
  };

  return (
    <div className="bg-sidebar-background flex min-h-screen justify-center">
      <div className="mt-60 flex w-full max-w-[488px] flex-col gap-6 px-4">
        <header className="flex flex-row items-center gap-2 text-3xl font-bold">
          <span>🙌</span>
          <h1>Let's get to know you</h1>
        </header>

        <section className="flex flex-col gap-2">
          <h2 className="text-foreground text-2xl leading-none font-medium tracking-tight">
            Just a few quick details
          </h2>
          <p className="text-muted-foreground text-2xl leading-none font-medium tracking-tight">
            Help us tailor Syncly for you.
          </p>
        </section>

        <form
          onSubmit={form.handleSubmit(handleSubmit)}
          className="flex max-w-[488px] flex-col gap-6"
        >
          <div className="flex flex-col gap-2">
            <label className="text-foreground text-sm font-medium">Company name</label>
            <div className="bg-background border-border relative w-full rounded-md border">
              <Input
                {...form.register('companyName')}
                placeholder="Type your company name"
                className="h-10 border-0 text-sm"
                disabled={createWorkspaceMutation.isPending}
              />
            </div>
            {form.formState.errors.companyName && (
              <p className="text-destructive text-sm">
                {form.formState.errors.companyName.message}
              </p>
            )}
          </div>

          <div className="flex flex-col gap-2">
            <label className="text-foreground text-sm font-medium">Job title</label>
            <div className="bg-background border-border relative w-full rounded-md border">
              <Input
                {...form.register('jobTitle')}
                placeholder=" Type your job title"
                className="h-10 border-0 text-sm"
                disabled={createWorkspaceMutation.isPending}
              />
            </div>
            {form.formState.errors.jobTitle && (
              <p className="text-destructive text-sm">{form.formState.errors.jobTitle.message}</p>
            )}
          </div>
        </form>

        <nav className="w-full rounded-md">
          <div className="flex items-center justify-end px-0 py-2">
            <Button
              className="h-10 px-4 text-sm font-medium"
              onClick={form.handleSubmit(handleSubmit)}
              disabled={!form.formState.isValid || createWorkspaceMutation.isPending}
            >
              <span>All set</span>
              <ArrowRight className="size-4" />
            </Button>
          </div>
        </nav>
      </div>
    </div>
  );
}
